import { when } from 'mobx';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CodeBlock, CopyToClipboardButton, Modal, Text, Well } from '@feathr/components';
import type { IRedirectDomainOption } from '@feathr/extender/components/RedirectDomainSelect';
import ContentServingDomainSelect from '@feathr/extender/components/RedirectDomainSelect/ContentServingDomainSelect';
import { getDonationFormHeadScript, useStore } from '@feathr/extender/state';

import { DomainAvailabilityStates } from '../../DonationFormEditPage/DonationFormBuilder/EmbedModal';

import * as styles from './EmbedScriptInstructionsModal.css';

interface IEmbedScriptInstructionsModalProps {
  opened: boolean;
  onClose: () => void;
}

function EmbedScriptInstructionsModal({
  opened,
  onClose,
}: Readonly<IEmbedScriptInstructionsModalProps>): JSX.Element {
  const { t } = useTranslation();
  const { Domains } = useStore();
  const [isLoadingDomains, setIsLoadingDomains] = useState<boolean>(false);
  const [availableContentDomains, setAvailableContentDomains] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const [redirectDomainId, setRedirectDomainId] = useState<string | undefined>(undefined);
  const [contentDomain, setContentDomain] = useState<string | undefined>(undefined);

  function handleSelectDomain({ id, name }: IRedirectDomainOption): void {
    setRedirectDomainId(id);
    setContentDomain(name);
  }

  // Track if code has been copied for this single snippet
  const [hasCodeBeenCopied, setHasCodeBeenCopied] = useState<boolean>(false);

  // Load available content domains
  useEffect(() => {
    async function loadDomains(): Promise<void> {
      // Check if we've already loaded domains - if so, skip the loading
      if (availableContentDomains.length > 0) {
        return;
      }

      setIsLoadingDomains(true);
      try {
        const domains = Domains.list();

        // Only wait for domains if they're not already loaded
        if (domains.isPending) {
          await when(() => !domains.isPending);
        }

        if (domains.isErrored) {
          setAvailableContentDomains([]);
          return;
        }

        const availableDomains = domains.models
          .filter((domain) => domain.isContentServingReady)
          .map((domain) => ({
            id: domain.id,
            name: domain.get('content_domain'),
          }));
        setAvailableContentDomains(availableDomains);

        // Auto-select domain if there's only one available
        if (availableDomains.length === 1 && !redirectDomainId) {
          const singleDomain = availableDomains[0];
          handleSelectDomain({
            id: singleDomain.id,
            name: singleDomain.name,
          });
        }
      } catch (error) {
        setAvailableContentDomains([]);
      } finally {
        setIsLoadingDomains(false);
      }
    }

    if (opened) {
      loadDomains();
    }
  }, [opened, redirectDomainId, handleSelectDomain, availableContentDomains]);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setRedirectDomainId(undefined);
      setContentDomain(undefined);
      setHasCodeBeenCopied(false);
      setIsLoadingDomains(false);
      setAvailableContentDomains([]);
    }
  }, [opened]);

  const hasAvailableDomains = availableContentDomains.length > 0;
  const domainCount = availableContentDomains.length;
  const showDomainSelect = domainCount > 1;

  const getModalRightActions = (): JSX.Element[] => {
    return [
      <Button key={'cancel'} onClick={onClose}>
        {t('Cancel')}
      </Button>,
      <Button disabled={!hasCodeBeenCopied} key={'done'} onClick={onClose} type={'primary'}>
        {t('Done')}
      </Button>,
    ];
  };

  return (
    <Modal
      allowOverflow={!contentDomain}
      description={'Add this embed script to your website to enable donation forms'}
      onClose={onClose}
      opened={opened}
      rightActions={getModalRightActions()}
      title={t('Website Embed Setup')}
    >
      <DomainAvailabilityStates
        hasAvailableDomains={hasAvailableDomains}
        isLoading={isLoadingDomains}
      />

      {/* Domains available - show selection and code */}
      {hasAvailableDomains && (
        <div className={styles.embedContent}>
          {showDomainSelect ? (
            <ContentServingDomainSelect
              label={t('Content serving domain')}
              onSelectSingle={handleSelectDomain}
              selectedDomainId={redirectDomainId}
            />
          ) : (
            <Well label={t('Content serving domain')} layout={'vertical'}>
              {contentDomain}
            </Well>
          )}

          {contentDomain && (
            <>
              <div className={styles.instructionsSection}>
                <Text.DefaultSemibold>{t('Installation Instructions')}</Text.DefaultSemibold>
                <ol className={styles.instructionsList}>
                  <li>{t('Copy the code snippet below')}</li>
                  <li>{t('Add it to the <head> section of your website')}</li>
                </ol>
              </div>

              <div className={styles.codeBlockWrapper}>
                <CodeBlock code={getDonationFormHeadScript(contentDomain)} language={'markup'} />
              </div>
              <CopyToClipboardButton
                className={styles.copyButton}
                name={'copy-embed-script'}
                onMouseDown={(): void => {
                  setHasCodeBeenCopied(true);
                }}
                t={t}
                text={getDonationFormHeadScript(contentDomain)}
                type={'secondary'}
              />

              <div className={styles.helpSection}>
                <Text.DefaultSemibold>{t('Need help?')}</Text.DefaultSemibold>
                <Text.DefaultRegular>
                  {t(
                    "The embed script should be added to every page where you want to display donation forms. It's safe to add it to your site's global header template.",
                  )}
                </Text.DefaultRegular>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

export default EmbedScriptInstructionsModal;
